/**
 * ONE TAP — client-side prediction and reconciliation.
 *
 * The local player must respond to input on the same frame the input happened,
 * at any ping. So the client runs the *exact* shared movement code the server
 * runs (`stepMovement`), keeps every unacknowledged command in a ring buffer,
 * and when the server's authoritative answer arrives it replays the pending
 * commands on top of it.
 *
 * Two rules make this feel invisible:
 *
 *  1. Only correct when the server actually disagrees. Float drift below
 *     RECONCILE_POSITION_THRESHOLD / RECONCILE_VELOCITY_THRESHOLD is ignored,
 *     otherwise you re-simulate constantly and jitter for no reason.
 *  2. Never teleport the camera. A correction is applied to the simulation
 *     immediately (the sim must stay authoritative) but the *rendered* position
 *     carries a decaying error offset, so the player walks the last few
 *     centimetres of the correction over ~100ms instead of snapping. A player
 *     should never see a movement they did not cause.
 *
 * Everything above the smoothing offset is transparent to callers: `local.pos`
 * always holds the position that should be rendered this instant, and the
 * offset is stripped and re-applied around every sim step internally.
 */

import { INPUT_BUFFER_SIZE, RECONCILE_POSITION_THRESHOLD, RECONCILE_VELOCITY_THRESHOLD } from '@shared/constants';
import { stepMovement } from '@shared/sim/movement';
import {
  add,
  clone,
  copy,
  distance,
  lengthSq,
  scale,
  set,
  sub,
  v3,
  type Vec3,
} from '@shared/math';
import {
  HealthState,
  RoundPhase,
  type CollisionWorld,
  type GameEvent,
  type InputCommand,
  type PlayerState,
  type WorldState,
} from '@shared/types';

/**
 * Decay rate of the visual error offset, in "fraction closed per second" (see
 * math.damp). 16 gives a 62ms time constant: a typical 6-20cm correction is
 * under a millimetre within ~150ms, which the eye reads as momentum rather than
 * a snap, while still being far too fast to let the render lag the simulation.
 */
const CORRECTION_SMOOTH_RATE = 16.0;
/** Corrections larger than this are teleports (respawn, round reset). Snap them honestly. */
const CORRECTION_SMOOTH_MAX = 1.5;
/** Below this the offset is dead; zero it so it can't linger as float noise. */
const CORRECTION_EPSILON = 1e-4;
/** Guard against a caller that never drains `events`. */
const EVENT_CAP = 128;

interface Entry {
  seq: number;
  cmd: InputCommand;
  /** State predicted *after* this command ran. Compared against the server's ack. */
  pos: Vec3;
  vel: Vec3;
}

function makeEntry(): Entry {
  return {
    seq: -1,
    cmd: { seq: -1, dt: 0, moveX: 0, moveZ: 0, yaw: 0, pitch: 0, buttons: 0, clientTimeMs: 0 },
    pos: v3(),
    vel: v3(),
  };
}

function copyCommand(dst: InputCommand, src: InputCommand): void {
  dst.seq = src.seq;
  dst.dt = src.dt;
  dst.moveX = src.moveX;
  dst.moveZ = src.moveZ;
  dst.yaw = src.yaw;
  dst.pitch = src.pitch;
  dst.buttons = src.buttons;
  dst.clientTimeMs = src.clientTimeMs;
}

/** Nullable-vector copy that never lets two players alias the same Vec3. */
function copyMaybeVec(dst: Vec3 | null, src: Vec3 | null): Vec3 | null {
  if (!src) return null;
  if (dst) return copy(dst, src);
  return clone(src);
}

/**
 * Every field `stepMovement` owns. On a correction these come from the server;
 * yaw and pitch are deliberately excluded — the mouse owns the camera, always,
 * and pulling the view from a 60ms-old snapshot is instantly nauseating.
 */
function copyPredictedFields(dst: PlayerState, src: PlayerState): void {
  copy(dst.pos, src.pos);
  copy(dst.vel, src.vel);
  dst.moveState = src.moveState;
  dst.grounded = src.grounded;
  dst.crouching = src.crouching;
  dst.wallNormal = copyMaybeVec(dst.wallNormal, src.wallNormal);
  dst.wallSurfaceId = src.wallSurfaceId;
  dst.wallRunElapsedMs = src.wallRunElapsedMs;
  dst.dashCharges = src.dashCharges;
  dst.dashCooldownMs = src.dashCooldownMs;
  dst.dashElapsedMs = src.dashElapsedMs;
  copy(dst.dashDir, src.dashDir);
  dst.slideElapsedMs = src.slideElapsedMs;
  dst.jumpsRemaining = src.jumpsRemaining;
  dst.coyoteMs = src.coyoteMs;
  dst.jumpBufferMs = src.jumpBufferMs;
  dst.grappleAnchorId = src.grappleAnchorId;
  dst.grapplePoint = copyMaybeVec(dst.grapplePoint, src.grapplePoint);
  dst.grappleLength = src.grappleLength;
  dst.grappleElapsedMs = src.grappleElapsedMs;
  dst.grappleCooldownMs = src.grappleCooldownMs;
  dst.grappleHookProgress = src.grappleHookProgress;
}

/**
 * Fields the server alone decides. These are adopted on every snapshot whether
 * or not the position needed correcting — health, the gun, and the combat
 * cooldowns are never predicted, because guessing them wrong is worse than
 * showing them a ping late.
 */
function copyAuthoritativeFields(dst: PlayerState, src: PlayerState): void {
  dst.name = src.name;
  dst.team = src.team;
  dst.bot = src.bot;
  dst.healthState = src.healthState;
  dst.health = src.health;
  dst.heldGun = src.heldGun;
  dst.fireCooldownMs = src.fireCooldownMs;
  dst.throwChargeMs = src.throwChargeMs;
  dst.catchLockoutMs = src.catchLockoutMs;
  dst.pulseCooldownMs = src.pulseCooldownMs;
  dst.kills = src.kills;
  dst.deaths = src.deaths;
  dst.catches = src.catches;
  dst.intercepts = src.intercepts;
  dst.connected = src.connected;
  dst.latencyMs = src.latencyMs;
}

/** Players are frozen at spawn outside of a live round (see RoundPhase.Warmup). */
function phaseAllowsMovement(w: WorldState): boolean {
  return w.match.phase === RoundPhase.Live;
}

export class Predictor {
  /**
   * Additive to the fixed contract: `stepMovement` emits jump/land/dash/wallrun
   * events, and the local player must hear and see those on the frame they
   * happen rather than a round-trip later. Predicted events land here; drain it
   * every frame. Replayed events are discarded, so nothing ever double-fires.
   */
  readonly events: GameEvent[] = [];

  /** Distance of the most recent correction, in metres. Useful for a netgraph. */
  lastCorrectionDistance = 0;
  /** Number of corrections applied since construction. */
  corrections = 0;

  private readonly world: CollisionWorld;
  private readonly ring: Entry[];
  private head = 0;
  private count = 0;
  /** Newest sequence the server has confirmed. Guards against stale/duplicate acks. */
  private lastAck = -1;

  /** Rendered position minus simulated position. Decays to zero over a few frames. */
  private readonly offset: Vec3 = v3();
  private offsetActive = false;

  /** Scratch sinks so replayed simulation can never emit duplicate feedback. */
  private readonly replayEvents: GameEvent[] = [];
  private readonly frozenCmd: InputCommand = {
    seq: 0,
    dt: 0,
    moveX: 0,
    moveZ: 0,
    yaw: 0,
    pitch: 0,
    buttons: 0,
    clientTimeMs: 0,
  };

  constructor(world: CollisionWorld) {
    this.world = world;
    this.ring = new Array<Entry>(INPUT_BUFFER_SIZE);
    for (let i = 0; i < INPUT_BUFFER_SIZE; i++) this.ring[i] = makeEntry();
  }

  /** Commands sent but not yet acknowledged by the server. */
  get pending(): number {
    let n = 0;
    for (let i = 0; i < this.count; i++) {
      if (this.ring[(this.head + i) % INPUT_BUFFER_SIZE]!.seq > this.lastAck) n++;
    }
    return n;
  }

  /** Wipe all prediction state. Call on (re)join or when the local id changes. */
  reset(): void {
    this.head = 0;
    this.count = 0;
    this.lastAck = -1;
    this.events.length = 0;
    this.replayEvents.length = 0;
    set(this.offset, 0, 0, 0);
    this.offsetActive = false;
    this.lastCorrectionDistance = 0;
  }

  /**
   * Run one command locally, right now, and remember it for replay.
   * `p` is the local player's state — the same object the renderer reads.
   */
  pushInput(cmd: InputCommand, p: PlayerState, w: WorldState): void {
    // Look always tracks the mouse, even while frozen or dead: the camera is the
    // one thing that must never wait on the network.
    p.yaw = cmd.yaw;
    p.pitch = cmd.pitch;

    // Work on the true simulated position, not the visually-smoothed one.
    this.stripOffset(p.pos);

    const alive = p.healthState !== HealthState.Dead;
    if (alive && phaseAllowsMovement(w)) {
      stepMovement(p, cmd, this.world, this.events);
      if (this.events.length > EVENT_CAP) this.events.splice(0, this.events.length - EVENT_CAP);
    } else {
      // Frozen at spawn or eliminated: the server still integrates the body, it
      // just ignores intent. Feed a neutral command so gravity and friction
      // agree with the server instead of drifting apart for the whole warmup.
      copyCommand(this.frozenCmd, cmd);
      this.frozenCmd.moveX = 0;
      this.frozenCmd.moveZ = 0;
      this.frozenCmd.buttons = 0;
      if (alive) stepMovement(p, this.frozenCmd, this.world, this.replayEvents);
      this.replayEvents.length = 0;
    }

    this.record(cmd, p);
    this.decayOffset(cmd.dt);
    this.applyOffset(p.pos);
  }

  /**
   * Fold the server's authoritative state for the local player back in.
   *
   * `serverP` is what the server says we were at `ackSeq`; `local` is our
   * predicted present. If they agree we keep predicting untouched; if they do
   * not, we adopt the server state and replay everything it hasn't seen yet.
   */
  reconcile(serverP: PlayerState, ackSeq: number, local: PlayerState, w: WorldState): void {
    if (!serverP || !local) return;

    // Non-predicted state is never argued with.
    copyAuthoritativeFields(local, serverP);

    // A snapshot older than one we already folded in (possible across a
    // reconnect) describes a past we have already replayed past. Applying it
    // would drag the player backwards for no reason.
    if (ackSeq < this.lastAck) return;

    // The mouse has moved since the command the server is describing; the view
    // must survive the correction untouched.
    const viewYaw = local.yaw;
    const viewPitch = local.pitch;

    this.stripOffset(local.pos);

    // Where we *thought* we'd be after the command the server just acked.
    const predicted = this.find(ackSeq);
    let diverged: boolean;
    if (predicted) {
      const dp = distance(predicted.pos, serverP.pos);
      const dv = distance(predicted.vel, serverP.vel);
      diverged = dp > RECONCILE_POSITION_THRESHOLD || dv > RECONCILE_VELOCITY_THRESHOLD;
      this.lastCorrectionDistance = dp;
    } else {
      // We have no record of that command: first snapshot after join, a buffer
      // overrun, or the server rejected our input stream. Trust the server.
      diverged = true;
      this.lastCorrectionDistance = distance(local.pos, serverP.pos);
    }

    // The dead don't get predicted; adopt the server's body wholesale.
    const dead = serverP.healthState === HealthState.Dead;

    if (diverged || dead) {
      const beforeX = local.pos.x;
      const beforeY = local.pos.y;
      const beforeZ = local.pos.z;

      copyPredictedFields(local, serverP);
      this.discardBefore(ackSeq);

      if (dead) {
        this.head = 0;
        this.count = 0;
      } else {
        // The anchor now holds the server's truth for `ackSeq`, so a duplicate
        // ack (routine whenever an input packet is lost) compares equal and
        // costs nothing instead of forcing another resim.
        if (predicted) {
          copy(predicted.pos, serverP.pos);
          copy(predicted.vel, serverP.vel);
        }
        this.replay(local, ackSeq, w);
      }
      this.corrections++;

      // Carry the visible discrepancy into the smoothing offset so the rendered
      // position is byte-identical across the correction and then eases home.
      const errX = beforeX + this.offset.x - local.pos.x;
      const errY = beforeY + this.offset.y - local.pos.y;
      const errZ = beforeZ + this.offset.z - local.pos.z;
      const errSq = errX * errX + errY * errY + errZ * errZ;
      if (dead || errSq > CORRECTION_SMOOTH_MAX * CORRECTION_SMOOTH_MAX) {
        // A genuine teleport. Smoothing a respawn across the arena would look
        // far worse than the cut.
        set(this.offset, 0, 0, 0);
        this.offsetActive = false;
      } else {
        set(this.offset, errX, errY, errZ);
        this.offsetActive = errSq > CORRECTION_EPSILON * CORRECTION_EPSILON;
      }
    } else {
      this.discardBefore(ackSeq);
    }

    this.applyOffset(local.pos);

    // `stepMovement` writes yaw/pitch from whatever command it replayed; the
    // camera belongs to the mouse, not to the network.
    local.yaw = viewYaw;
    local.pitch = viewPitch;
    this.lastAck = ackSeq;
  }

  // -------------------------------------------------------------------------
  // Ring buffer
  // -------------------------------------------------------------------------

  private record(cmd: InputCommand, p: PlayerState): void {
    if (this.count === INPUT_BUFFER_SIZE) {
      // Buffer full means the server has gone quiet for two seconds at 30Hz.
      // Drop the oldest; the next snapshot will resync us anyway.
      this.head = (this.head + 1) % INPUT_BUFFER_SIZE;
      this.count--;
    }
    const e = this.ring[(this.head + this.count) % INPUT_BUFFER_SIZE]!;
    e.seq = cmd.seq;
    copyCommand(e.cmd, cmd);
    copy(e.pos, p.pos);
    copy(e.vel, p.vel);
    this.count++;
  }

  private find(seq: number): Entry | null {
    for (let i = 0; i < this.count; i++) {
      const e = this.ring[(this.head + i) % INPUT_BUFFER_SIZE]!;
      if (e.seq === seq) return e;
    }
    return null;
  }

  /**
   * Drop everything the server has already superseded, but KEEP the acked
   * command itself. It is the anchor the next ack is compared against, and
   * without it a repeated ack looks like "no record" and triggers a needless
   * re-simulation on every dropped input packet.
   */
  private discardBefore(seq: number): void {
    while (this.count > 0) {
      const e = this.ring[this.head]!;
      if (e.seq >= seq) break;
      this.head = (this.head + 1) % INPUT_BUFFER_SIZE;
      this.count--;
    }
  }

  /** Re-run every command the server has not seen yet, on top of its state. */
  private replay(local: PlayerState, ackSeq: number, w: WorldState): void {
    const live = local.healthState !== HealthState.Dead && phaseAllowsMovement(w);
    for (let i = 0; i < this.count; i++) {
      const e = this.ring[(this.head + i) % INPUT_BUFFER_SIZE]!;
      if (e.seq <= ackSeq) continue; // already baked into the server state
      if (live) {
        stepMovement(local, e.cmd, this.world, this.replayEvents);
      } else {
        copyCommand(this.frozenCmd, e.cmd);
        this.frozenCmd.moveX = 0;
        this.frozenCmd.moveZ = 0;
        this.frozenCmd.buttons = 0;
        stepMovement(local, this.frozenCmd, this.world, this.replayEvents);
      }
      // Keep the buffer's predictions in sync with the corrected timeline so the
      // next ack compares against the same history the server will describe.
      copy(e.pos, local.pos);
      copy(e.vel, local.vel);
      this.replayEvents.length = 0;
    }
  }

  // -------------------------------------------------------------------------
  // Visual smoothing
  // -------------------------------------------------------------------------

  private stripOffset(pos: Vec3): void {
    if (!this.offsetActive) return;
    sub(pos, pos, this.offset);
  }

  private applyOffset(pos: Vec3): void {
    if (!this.offsetActive) return;
    add(pos, pos, this.offset);
  }

  private decayOffset(dt: number): void {
    if (!this.offsetActive) return;
    scale(this.offset, this.offset, Math.exp(-CORRECTION_SMOOTH_RATE * dt));
    if (lengthSq(this.offset) < CORRECTION_EPSILON * CORRECTION_EPSILON) {
      set(this.offset, 0, 0, 0);
      this.offsetActive = false;
    }
  }
}
